import {
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { ObserveOptions } from './observe';
import { useReveal } from './useReveal';

export type RevealVariant = 'blur' | 'focus' | 'rise';

/**
 * Scroll-triggered reveal primitives (DESIGN_SYSTEM §3, MOT-01/02/03). The motion itself is CSS
 * (`styles/motion.css`: opacity · blur · translate on the compositor); this module only decides
 * *when* — a shared IntersectionObserver flips `data-revealed` once.
 *
 *   blur   opacity 0 · blur(10px) · rise 28px → clear, 1.4 s ease-reveal   (headings, hero copy, media)
 *   rise   opacity 0 · rise 32px · scale .98 · blur(8px) → clear, .4 s     (cards, list items)
 *   focus  opacity 0 · blur(6px) → clear, .4 s                              (small captions)
 *
 * Quality tiers are handled in CSS: `lite` drops the blur, `minimal`/reduced-motion shows a ≤150 ms
 * fade. Without JS, without IntersectionObserver, or before the app has booted the content is simply
 * visible — reveals never hide anything permanently.
 */

type RevealOwnProps<T extends ElementType> = {
  /** The element to render. Default `div`. */
  as?: T;
  variant?: RevealVariant;
  /** Extra delay in ms (staggering). */
  delay?: number;
  children?: ReactNode;
} & ObserveOptions;

export type RevealProps<T extends ElementType = 'div'> = RevealOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof RevealOwnProps<T>>;

export function Reveal<T extends ElementType = 'div'>({
  as,
  variant = 'blur',
  delay = 0,
  once,
  rootMargin,
  threshold,
  style,
  children,
  ...rest
}: RevealProps<T>): ReactElement {
  const Component: ElementType = as ?? 'div';
  const ref = useReveal<HTMLElement>({
    ...(once === undefined ? {} : { once }),
    ...(rootMargin === undefined ? {} : { rootMargin }),
    ...(threshold === undefined ? {} : { threshold }),
  });
  const merged = {
    ...(style as CSSProperties | undefined),
    ...(delay > 0 ? { '--reveal-delay': `${String(delay)}ms` } : {}),
  } as CSSProperties;
  return (
    <Component ref={ref} data-reveal={variant} style={merged} {...rest}>
      {children}
    </Component>
  );
}

export interface StackRevealProps {
  as?: ElementType;
  className?: string;
  /** Delay between children in ms. Default 80. */
  stagger?: number;
  children: ReactNode;
}

/**
 * Children appear one after another (MOT-02). The wrapper is observed once; its *direct children*
 * get a CSS index (`--i`) and stagger through `transition-delay` — no extra wrapper elements, so grid
 * and flex layouts keep working. Non-element children (text) are left as they are.
 */
export function StackReveal({
  as: Wrapper = 'div',
  className,
  stagger = 80,
  children,
}: StackRevealProps): ReactElement {
  const ref = useReveal<HTMLElement>();
  return (
    <Wrapper
      ref={ref}
      data-stack-reveal=""
      className={className}
      style={{ '--stack-step': `${String(stagger)}ms` } as CSSProperties}
    >
      {Children.map(children, (child, index) =>
        isValidElement<{ style?: CSSProperties }>(child)
          ? cloneElement(child, {
              style: { ...child.props.style, '--i': index } as CSSProperties,
            })
          : child,
      )}
    </Wrapper>
  );
}
